import { addStyles } from 'ethcss'
import { color, IStyles } from 'theme'

const templateEditorLeftPanelsStyles: IStyles = {
    leftPanels: {
        width: 336,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        background: color.bgWhitePrimary,
        borderRight: `1px solid ${color.borderDarkPrimary}`,
        minHeight: 0,
        overflow: 'hidden',
    },
    leftSplit: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    templatesTools: {
        padding: '8px 12px 0',
        flexShrink: 0,
    },
    templatesSearch: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: 10,
        background: color.bgWhitePrimary,
        padding: '0 10px',
        minHeight: 34,
    },
    templatesSearchIcon: {
        fontSize: 12,
        opacity: 0.55,
        flexShrink: 0,
    },
    templatesSearchInput: {
        flex: 1,
        border: 'none',
        background: 'transparent',
        fontFamily: 'inherit',
        fontSize: 12,
        color: color.fontDarkUltraHigh,
        minWidth: 0,
        outline: 'none',
    },
    templatesGrid: {
        flex: '1 1 0%',
        minHeight: 0,
        height: '100%',
        padding: '10px 12px 12px',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gridAutoRows: 'max-content',
        gap: 10,
        overflowX: 'hidden',
        overflowY: 'auto',
        alignContent: 'start',
        alignItems: 'start',
        overscrollBehavior: 'contain',
    },
    templatesEmpty: {
        padding: '16px 12px 20px',
        fontSize: 12,
        color: color.fontDarkLow,
        textAlign: 'center',
        lineHeight: 1.45,
        gridColumn: '1 / -1',
        flexShrink: 0,
    },
    templatesEmptyHint: {
        marginTop: 6,
        fontSize: 11,
        color: color.fontDarkUltraLow,
        lineHeight: 1.4,
    },
    templateCard: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: 10,
        background: color.bgWhitePrimary,
        overflow: 'hidden',
        textAlign: 'left',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        alignSelf: 'start',
        width: '100%',
        flexShrink: 0,
        minWidth: 0,

        '&:hover': {
            borderColor: color.blue400,
            boxShadow: `0 4px 14px ${color.blue200}`,
        },

        '&:hover [data-template-delete]': {
            opacity: 1,
        },
    },
    templateCardActive: {
        borderColor: color.blue,
        boxShadow: `0 0 0 2px ${color.bgWhitePrimary}, 0 0 0 4px ${color.blue}`,
    },
    templateCardBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        width: '100%',
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
        textAlign: 'left',
        flex: '0 0 auto',
        minHeight: 'auto',

        '&:disabled': {
            opacity: 0.7,
            cursor: 'wait',
        },
    },
    templateCardDelete: {
        position: 'absolute',
        top: 4,
        right: 4,
        zIndex: 2,
        boxSizing: 'border-box',
        width: 28,
        height: 28,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius: 8,
        background: 'rgba(255, 255, 255, 0.95)',
        boxShadow: `0 1px 4px ${color.borderDarkPrimary}`,
        cursor: 'pointer',
        opacity: 0,
        transition: 'opacity 0.15s, background 0.15s',
        padding: 0,
        margin: 0,
        lineHeight: 0,

        '& > div': {
            position: 'relative',
            display: 'block',
            width: 12,
            height: 14,
            lineHeight: 0,
            margin: 0,
            flex: '0 0 auto',
        },

        '& svg': {
            position: 'absolute',
            left: '50%',
            top: '50%',
            display: 'block',
            width: 12,
            height: 14,
            margin: 0,
            transform: 'translate(-50%, -50%)',
        },

        '&:hover': {
            background: color.bgWhitePrimary,
        },

        '&:focus-visible': {
            opacity: 1,
        },
    },
    templateCardThumb: {
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        flex: '0 0 auto',
        background: color.bgWhiteTertiary,
        overflow: 'hidden',
    },
    templateCardImage: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    templateCardPlaceholder: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        color: color.fontDarkLow,
    },
    templateCardName: {
        padding: '0 8px 8px',
        fontSize: 11,
        lineHeight: '14px',
        color: color.fontDarkUltraHigh,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: '0 0 auto',
    },
    templatesLoadMore: {
        gridColumn: '1 / -1',
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: 8,
        background: color.bgWhitePrimary,
        padding: '8px 10px',
        fontSize: 12,
        color: color.blue,
        cursor: 'pointer',
    },
    catalogToolbar: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '10px 12px 8px',
        flexShrink: 0,
        borderBottom: `1px solid ${color.borderDarkPrimary}`,
        background: color.bgWhitePrimary,
    },
    panelTabs: {
        display: 'flex',
        alignItems: 'stretch',
        gap: 0,
        borderBottom: `1px solid ${color.borderDarkPrimary}`,
    },
    panelTab: {
        flex: 1,
        padding: '8px 4px 9px',
        marginBottom: -1,
        border: 'none',
        borderBottom: '2px solid transparent',
        background: 'transparent',
        borderRadius: 0,
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: 500,
        color: color.fontDarkLow,
        cursor: 'pointer',
        transition: 'color 0.15s, border-color 0.15s',

        '&:hover': {
            color: color.blue,
        },
    },
    panelTabActive: {
        color: color.blue,
        fontWeight: 600,
        borderBottomColor: color.blue,
        background: 'transparent',
        boxShadow: 'none',
    },
    catalogSearchWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: 8,
        background: color.bgWhiteSecondary,
        padding: '0 10px',
        minHeight: 34,
    },
    catalogSearchIcon: {
        fontSize: 12,
        opacity: 0.55,
        flexShrink: 0,
    },
    catalogSearch: {
        flex: 1,
        margin: 0,
        padding: '8px 0',
        border: 'none',
        borderRadius: 0,
        fontFamily: 'inherit',
        fontSize: 12,
        width: 'auto',
        minWidth: 0,
        background: 'transparent',
        outline: 'none',
        color: color.fontDarkUltraHigh,
    },
    catalogBreadcrumbs: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '2px 0',
        padding: 0,
        fontSize: 11,
        lineHeight: '16px',
        minWidth: 0,
    },
    catalogBreadcrumbItem: {
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 0,
        maxWidth: '100%',
    },
    catalogBreadcrumbSep: {
        margin: '0 6px',
        color: color.fontDarkUltraLow,
        flexShrink: 0,
    },
    catalogBreadcrumbLink: {
        border: 'none',
        background: 'transparent',
        padding: 0,
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 500,
        color: color.blue600,
        cursor: 'pointer',
        maxWidth: 120,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',

        '&:hover': {
            color: color.blue,
            textDecoration: 'underline',
        },
    },
    catalogBreadcrumbCurrent: {
        fontWeight: 600,
        color: color.fontDarkUltraHigh,
        maxWidth: 140,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    catalogGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 8,
        padding: '10px 12px 12px',
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
        alignContent: 'start',
    },
    catalogGridReplaceMode: {
        boxShadow: `inset 0 0 0 2px ${color.blue}`,
        background: color.blue100,
    },
    catalogEmpty: {
        gridColumn: '1 / -1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '20px 12px',
        fontSize: 12,
        color: color.fontDarkLow,
        textAlign: 'center',
    },
    catalogSpinner: {
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: `2px solid ${color.borderDarkPrimary}`,
        borderTopColor: color.blue,
        animation: 'spin 0.7s linear infinite',
    },
    catalogItem: {
        position: 'relative',
        width: '100%',
        height: 0,
        paddingBottom: '100%',
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${color.borderDarkPrimary}`,
        cursor: 'pointer',
        background: color.bgWhiteTertiary,
        boxShadow: `0 2px 8px ${color.bgAlphaDark50}`,
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.1s',
        padding: 0,
        textAlign: 'left',

        '&:hover': {
            borderColor: color.blue400,
            boxShadow: `0 4px 14px ${color.blue200}`,
            transform: 'translateY(-1px)',
        },
    },
    catalogItemActive: {
        borderColor: color.blue,
        boxShadow: `0 0 0 2px ${color.bgWhitePrimary}, 0 0 0 4px ${color.blue}, 0 10px 22px ${color.blue200}`,
        transform: 'translateY(-1px)',
        zIndex: 1,

        '&:hover': {
            borderColor: color.blue,
            boxShadow: `0 0 0 2px ${color.bgWhitePrimary}, 0 0 0 4px ${color.blue}, 0 12px 24px ${color.blue200}`,
            transform: 'translateY(-1px)',
        },
    },
    catalogItemDisabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
        pointerEvents: 'none',
    },
    catalogItemCheck: {
        position: 'absolute',
        top: 6,
        right: 6,
        zIndex: 2,
        width: 20,
        height: 20,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: color.blue,
        color: color.bgWhitePrimary,
        boxShadow: `0 2px 8px ${color.bgAlphaDark50}`,
        pointerEvents: 'none',
    },
    catalogItemName: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        padding: '4px 6px',
        fontSize: 10,
        lineHeight: '12px',
        color: color.bgWhitePrimary,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    catalogThumbImage: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    catalogThumb: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
    },
    catalogThumbFolder: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: color.bgWhiteSecondary,
        color: color.iconDarkMedium,

        '& > div': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
        },

        '& svg': {
            display: 'block',
        },
    },
    catalogThumbFallback: {
        background: `linear-gradient(135deg, ${color.blue100}, ${color.info100})`,
    },
    catalogThumbP1: { background: `linear-gradient(135deg, ${color.blue}, ${color.blue300})` },
    catalogThumbP2: { background: `linear-gradient(135deg, ${color.critical}, ${color.critical200})` },
    catalogThumbP3: { background: `linear-gradient(135deg, ${color.warning}, ${color.warning200})` },
    catalogThumbP4: { background: `linear-gradient(135deg, ${color.info}, ${color.info200})` },
    catalogThumbP5: { background: `linear-gradient(135deg, ${color.success}, ${color.success200})` },
    catalogThumbP6: { background: `linear-gradient(135deg, ${color.blueHover}, ${color.blue400})` },
    layersList: {
        listStyle: 'none',
        margin: 0,
        padding: 6,
        overflowX: 'hidden',
        overflowY: 'auto',
        flex: '1 1 0',
        minHeight: 0,
        height: 0,
        width: '100%',
        boxSizing: 'border-box',
    },
    layerItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        padding: '7px 8px 7px 11px',
        borderRadius: 8,
        fontSize: 12,
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative',
        border: '1px solid transparent',
        transition: 'background 0.12s, border-color 0.12s, color 0.12s',
        background: color.bgWhitePrimary,

        '&:hover': {
            background: color.bgWhiteSecondary,
        },
    },
    layerItemDragging: {
        opacity: 0.45,
    },
    layerItemDropTarget: {
        borderColor: color.blue,
        background: color.blue100,
    },
    layerItemSelected: {
        background: color.blue100,
        color: color.blue600,
        borderColor: color.blue,
        fontWeight: 600,

        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 6,
            bottom: 6,
            width: 3,
            borderRadius: '0 2px 2px 0',
            background: color.blue,
        },

        '&:hover': {
            background: color.blue100,
            color: color.blue600,
            borderColor: color.blue,
        },
    },
    layerItemHidden: {
        opacity: 0.45,
    },
    layerItemDrag: {
        flexShrink: 0,
        color: color.borderDarkTertiary,
        cursor: 'grab',
        fontSize: 10,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,

        '&:active': {
            cursor: 'grabbing',
        },
    },
    layerItemDragSpacer: {
        flexShrink: 0,
        width: 16,
        height: 16,
    },
    layerExpand: {
        flexShrink: 0,
        width: 16,
        height: 16,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
        color: color.fontDarkLow,
        borderRadius: 4,
        transform: 'translateY(-1px)',

        '&:hover': {
            color: color.fontDarkUltraHigh,
            background: color.bgWhiteSecondary,
        },
    },
    layerExpandSpacer: {
        flexShrink: 0,
        width: 16,
        height: 16,
    },
    layerItemName: {
        flex: '1 1 0',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    layerAction: {
        flexShrink: 0,
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: 'transparent',
        borderRadius: 4,
        cursor: 'pointer',
        padding: 0,
        color: color.fontDarkUltraHigh,
        transition: 'background 0.12s ease, color 0.12s ease, opacity 0.12s ease',
        opacity: 1,

        '&:hover': {
            background: color.bgWhitePrimary,
            color: color.fontDarkUltraHigh,
            opacity: 1,
            boxShadow: `0 0 0 1px ${color.borderDarkTertiary}`,
        },

        '&:active': {
            background: color.bgWhiteSecondary,
        },

        '&:disabled': {
            cursor: 'default',
            opacity: 1,
        },
    },
    layerActionActive: {
        color: color.blue,
        opacity: 1,
    },
}

addStyles(templateEditorLeftPanelsStyles)
export default templateEditorLeftPanelsStyles
